// @file Composable to get rotated generic loading messages for the Surface AI chat
import { createSharedComposable } from '@vueuse/core'
import { ref } from 'vue'

import { __, p__ } from '@@/bits/intl'

enum LoadingMessageKey {
  CREATING_POSTS = 'creating_posts',
  SEARCHING_THE_WEB = 'searching_the_web',
  EXPORTING_BOARD = 'exporting_board',
  EXPORTING_WHITEBOARD = 'exporting_whiteboard',
  ANALYZING_ATTACHMENTS = 'analyzing_attachments',
  GRADING_POSTS = 'grading_posts',
  SYNCING_GRADES = 'syncing_grades',
  SEARCHING_PADLETS = 'searching_padlets',
  REMAKING_PADLET = 'remaking_padlet',
  RESTORING_CONTENT = 'restoring_content',
  INVITING_MEMBERS = 'inviting_members',
  SENDING_INVITATIONS = 'sending_invitations',
  CREATING_POST_CONNECTIONS = 'creating_post_connections',
  DELETING_POST_CONNECTIONS = 'deleting_post_connections',
}

const GENERIC_MESSAGES: string[] = [
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Thinking'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Orbiting'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Stargazing'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Launching'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Warping'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Floating'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Drifting'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Scanning'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Rocketing'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Beaming'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Transmitting'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Hyperdriving'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Blasting'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Zooming'),
  p__('Used as a loading message for an AI chatbot while it is thinking', 'Teleporting'),
]

const MESSAGES: Record<LoadingMessageKey, string> = {
  [LoadingMessageKey.CREATING_POSTS]: __('Creating posts'),
  [LoadingMessageKey.SEARCHING_THE_WEB]: __('Searching the web'),
  [LoadingMessageKey.EXPORTING_BOARD]: __('Exporting board'),
  [LoadingMessageKey.EXPORTING_WHITEBOARD]: __('Exporting sandbox'),
  [LoadingMessageKey.ANALYZING_ATTACHMENTS]: __('Analyzing attachments'),
  [LoadingMessageKey.GRADING_POSTS]: __('Grading posts'),
  [LoadingMessageKey.SYNCING_GRADES]: __('Syncing grades'),
  [LoadingMessageKey.SEARCHING_PADLETS]: __('Searching padlets'),
  [LoadingMessageKey.REMAKING_PADLET]: __('Remaking padlet'),
  [LoadingMessageKey.RESTORING_CONTENT]: __('Restoring content'),
  [LoadingMessageKey.INVITING_MEMBERS]: __('Inviting members'),
  [LoadingMessageKey.SENDING_INVITATIONS]: __('Sending invitations'),
  [LoadingMessageKey.CREATING_POST_CONNECTIONS]: __('Creating post connections'),
  [LoadingMessageKey.DELETING_POST_CONNECTIONS]: __('Deleting post connections'),
}

const useSurfaceAiChatLoadingMessages = createSharedComposable(() => {
  const remainingMessages = ref<string[]>([...GENERIC_MESSAGES])
  const lastMessage = ref<string | null>(null)

  /**
   * Get a random message from the remaining messages, without repeating consecutive messages.
   * @returns A random message from the remaining messages
   */
  const getRandomGenericMessage = (): string => {
    const randomIndex = Math.floor(Math.random() * remainingMessages.value.length)
    const message = remainingMessages.value.splice(randomIndex, 1)[0]
    lastMessage.value = message
    if (remainingMessages.value.length === 0) {
      const newMessages = [...GENERIC_MESSAGES]
      const lastIndex = newMessages.findIndex((m) => m === lastMessage.value)
      if (lastIndex !== -1) newMessages.splice(lastIndex, 1)
      remainingMessages.value = newMessages
    }
    return message
  }

  const getLoadingMessage = (key: string | null): string => {
    if (key != null && key in MESSAGES) {
      return MESSAGES[key as LoadingMessageKey]
    }
    return getRandomGenericMessage()
  }

  return { getLoadingMessage }
})

export { GENERIC_MESSAGES, MESSAGES, useSurfaceAiChatLoadingMessages }
